import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { isEmpty, isNil } from 'lodash'
import queryString from 'query-string'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'

import Select from '@/components/forms/Select'
import { Banner } from '@/components/banners/Banner'
import { FormField } from '@/components/forms/FormField'
import {
  fetchRTNMetrics,
  getSummaryConfigForPlaybook,
  fetchOrgFieldNames,
} from '@/reducers/playbooks/playbook.actions'
import { fetchPlaybooksByOrg } from '@/reducers/analytics/analytics.actions'
import { setRTNOrg, setRTNPrompt, setRTNBitFlow } from '@/reducers/playbooks/playbook.redux'
import {
  getOrganizationOptions,
  getChannelTypeOptions,
  hasAnyOmnichannelAccess,
  hasChannelAccess,
} from '@/utils/helpers'
import { CHANNEL_TYPE } from '@/utils/constants'

const PlaybookFormComponent = ({ setFieldValue, values, onChannelChange }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { organizationid } = useSelector((state) => state.currentUser)
  const analyticsStore = useSelector((state) => state.analytics)
  const { data, loading: analyticsLoading } = analyticsStore
  const { organizations } = useSelector((state) => state)
  const currentOrganization = useSelector((state) => state.currentOrganization)

  const organizationOptions = useMemo(() => getOrganizationOptions(organizations), [organizations])

  const isAdmin = organizationid === 1
  // Parse the shared-URL filters once (useRef would re-run its arg every render).
  const restoreRef = useRef()
  if (isNil(restoreRef.current)) {
    restoreRef.current = queryString.parse(location.search)
  }
  // loading.playbooks is false both before the fetch and after it, so latch
  // once we've seen it run — lets the restore effect tell "not started" from
  // "done (maybe empty)". Never reset; a remount gets a fresh ref.
  const playbookFetchStartedRef = useRef(false)
  // True while a shared link's filters are still being restored, so the mirror
  // effect waits (and can react when restore finishes — clearing a ref can't).
  const [restorePending, setRestorePending] = useState(() => !isEmpty(restoreRef.current))

  const [selectedOrg, setSelectedOrg] = useState(() => {
    const orgId = Number(restoreRef.current.org)
    return isAdmin && Number.isInteger(orgId) && orgId > 0 ? orgId : null
  })

  // Resolve the selected org object for admin lookups
  const selectedOrgData = isAdmin
    ? organizations.find((org) => org.organizationid === selectedOrg || org.id === selectedOrg)
    : currentOrganization

  const activeChannels = isAdmin
    ? selectedOrgData?.active_channels
    : currentOrganization?.active_channels
  const notetakerEnabled = isAdmin ? selectedOrgData?.call_summarization_enabled !== false : true

  const hasOmnichannelAccess = hasAnyOmnichannelAccess(activeChannels)

  const updateChannel = (channel) => {
    setFieldValue('selected_channel', channel)
    onChannelChange?.(channel)
  }

  // If no omni access, force channel to call
  useEffect(() => {
    if (!hasOmnichannelAccess && values.selected_channel !== CHANNEL_TYPE.CALL) {
      updateChannel(CHANNEL_TYPE.CALL)
    }
  }, [hasOmnichannelAccess, values.selected_channel])

  // Load playbooks + metrics on mount and when org changes (Call; restore
  // effect applies any shared channel).
  useEffect(() => {
    updateChannel(CHANNEL_TYPE.CALL)

    if (isAdmin) {
      if (selectedOrg && notetakerEnabled) {
        dispatch(fetchPlaybooksByOrg(selectedOrg))
        dispatch(fetchRTNMetrics({ organizationId: selectedOrg, callType: CHANNEL_TYPE.CALL }))
        dispatch(fetchOrgFieldNames(selectedOrg))
      }
    } else {
      dispatch(fetchPlaybooksByOrg(organizationid))
      dispatch(fetchRTNMetrics({ callType: CHANNEL_TYPE.CALL }))
      dispatch(fetchOrgFieldNames(organizationid))
    }
  }, [selectedOrg, notetakerEnabled])

  const clearFormData = () => {
    dispatch(setRTNPrompt(''))
    dispatch(setRTNBitFlow(null))
    setFieldValue('selected_playbook_cid', '')
    setFieldValue('prompt_fields', [])
  }

  const handlePlaybookChange = (playbook, channel = values.selected_channel) => {
    setFieldValue('selected_playbook_cid', playbook.value)
    dispatch(setRTNPrompt(''))
    dispatch(setRTNBitFlow(null))
    if (isAdmin) {
      dispatch(
        fetchRTNMetrics({
          organizationId: selectedOrg,
          playbookCid: playbook.value,
          callType: channel,
        })
      )
      dispatch(setRTNOrg(selectedOrg))
    } else {
      dispatch(fetchRTNMetrics({ playbookCid: playbook.value, callType: channel }))
    }
    if (channel && playbook.value) {
      dispatch(getSummaryConfigForPlaybook(playbook.value, channel))
    }
    // Refresh the reuse list so field names added to other playbooks (here or
    // by another user) show up as suggestions on this one.
    dispatch(fetchOrgFieldNames(isAdmin ? selectedOrg : organizationid))
  }

  const handleOrgSelect = (dropdownOrg) => {
    setSelectedOrg(dropdownOrg.value)
    clearFormData()

    const selected = organizations.find(
      (org) => org.organizationid === dropdownOrg.value || org.id === dropdownOrg.value
    )
    if (!hasChannelAccess(selected?.active_channels, values.selected_channel)) {
      updateChannel(CHANNEL_TYPE.CALL)
    }

    dispatch(setRTNOrg(dropdownOrg.value))
  }

  const handleChannelChange = (channel) => {
    if (!hasOmnichannelAccess && channel.value !== CHANNEL_TYPE.CALL) {
      updateChannel(CHANNEL_TYPE.CALL)
      clearFormData()
      return
    }

    updateChannel(channel.value)
    dispatch(setRTNPrompt(''))
    dispatch(setRTNBitFlow(null))

    if (isAdmin) {
      dispatch(
        fetchRTNMetrics({
          organizationId: selectedOrg,
          playbookCid: values.selected_playbook_cid,
          callType: channel.value,
        })
      )
      dispatch(setRTNOrg(selectedOrg))
    } else {
      dispatch(
        fetchRTNMetrics({ playbookCid: values.selected_playbook_cid, callType: channel.value })
      )
    }
    if (channel.value && values.selected_playbook_cid) {
      dispatch(getSummaryConfigForPlaybook(values.selected_playbook_cid, channel.value))
    }
  }
  // Apply the shared channel + playbook once the data to validate them has
  // loaded: activeChannels (lazy for admins) and data.playbooks.
  useEffect(() => {
    if (analyticsLoading.playbooks) {
      playbookFetchStartedRef.current = true
    }

    const { channel, playbook } = restoreRef.current
    if (isEmpty(restoreRef.current)) {
      return
    }
    if (!isNil(channel) && isNil(activeChannels)) {
      return
    }
    // Stop waiting once the fetch settles, or if none is expected (see the mount
    // effect) — else an unknown playbook, or an org-less admin link, hangs here.
    const playbookFetchExpected = !isAdmin || (!isNil(selectedOrg) && notetakerEnabled)
    const playbookFetchSettled = playbookFetchStartedRef.current && !analyticsLoading.playbooks
    if (
      !isNil(playbook) &&
      isEmpty(data.playbooks) &&
      playbookFetchExpected &&
      !playbookFetchSettled
    ) {
      return
    }

    const restoredChannel = hasChannelAccess(activeChannels, channel) ? channel : CHANNEL_TYPE.CALL
    const match = data.playbooks.find((option) => option.value === playbook)
    restoreRef.current = {}
    setRestorePending(false)

    updateChannel(restoredChannel)
    if (!isNil(match)) {
      handlePlaybookChange(match, restoredChannel)
    }
  }, [activeChannels, data.playbooks, analyticsLoading.playbooks])

  useEffect(() => {
    // Wait for any shared-link restore first: a cold load could strip params
    // before restore runs, or leave a now-invalid channel in the URL after.
    if (restorePending) {
      return
    }
    const params = {}
    if (isAdmin && !isNil(selectedOrg)) {
      params.org = selectedOrg
    }
    // No channel until the org can pick one — keeps a fresh admin page param-free.
    if (hasOmnichannelAccess && !isEmpty(values.selected_channel)) {
      params.channel = values.selected_channel
    }
    if (!isEmpty(values.selected_playbook_cid)) {
      params.playbook = values.selected_playbook_cid
    }
    history.replace({ pathname: location.pathname, search: queryString.stringify(params) })
  }, [
    restorePending,
    selectedOrg,
    hasOmnichannelAccess,
    values.selected_channel,
    values.selected_playbook_cid,
  ])

  const availableChannelOptions = getChannelTypeOptions(activeChannels)

  const showChannelSelector = isAdmin || hasOmnichannelAccess

  return (
    <div className="rtn-editor__prompt">
      <h2>{showChannelSelector ? 'Select a Channel' : 'Select a Playbook'}</h2>
      <p className="muted-text">
        {showChannelSelector
          ? 'Select a channel to customize how Balto generates notes for your conversations.'
          : 'Choose a Playbook to begin editing the AI Notetaker instructions'}
      </p>

      <div className="rtn-load-options">
        {isAdmin && (
          <FormField>
            <label>Organization</label>
            <div data-testid="org-dropdown">
              <Select
                options={organizationOptions}
                label="Organization"
                value={selectedOrg}
                onChange={handleOrgSelect}
                placeholder="Select Organization"
                isClearable={false}
                fixedWidth
                data-testid="org-dropdown"
              />
            </div>
          </FormField>
        )}

        {isAdmin && selectedOrg && !notetakerEnabled && (
          <Banner warning dataTestId="notetaker-not-enabled-banner">
            This organization does not have Notetaker enabled.
          </Banner>
        )}

        {notetakerEnabled && showChannelSelector && (
          <FormField>
            <label>Channel</label>
            <div data-testid="channel-dropdown">
              <Select
                options={availableChannelOptions}
                label="Channel"
                value={values.selected_channel}
                onChange={handleChannelChange}
                placeholder="Select Channel"
                isClearable={false}
                fixedWidth
                isDisabled={isAdmin && !selectedOrg}
              />
            </div>
          </FormField>
        )}

        {notetakerEnabled && (
          <FormField>
            <label>Playbook</label>
            <div data-testid="playbook-dropdown">
              <Select
                placeholder="Select Playbook"
                loading={analyticsLoading.playbooks}
                options={data.playbooks}
                label="Playbook"
                value={values.selected_playbook_cid}
                onChange={(playbook) => handlePlaybookChange(playbook)}
                className="playbook-select"
                isClearable={false}
                fixedWidth
                isDisabled={isAdmin && !selectedOrg}
              />
            </div>
          </FormField>
        )}
      </div>
    </div>
  )
}
export const PlaybookForm = withLDConsumer()(PlaybookFormComponent)
